import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useI18n } from "components/I18nProvider";
import SubtitlesHeader from "./SubtitlesHeader";
import SubtitlesPanel from "../../components/SubtitlesPanel";
import SubtitlesFooter from "./SubtitlesFooter";
import styles from './style.module.scss';
import { loadSubtitlesSettings, storeSubtitlesSettings } from "./subtitles-settings";
import { IdleTimer } from "./idleTimer";
import AlertPanel from "./AlertPanel";
import { AudioMutedOutlined, CloseCircleFilled, Loading3QuartersOutlined } from "@ant-design/icons";
import { fetchPublicEventById } from "api/events";
import { buildUrlPath } from "lib/resolveUrl";
import { isTestingMode } from "components/TestingMode";
import { SubtitlesStream } from "lib/subtitles-stream";
const idleTimer = new IdleTimer();
const updateTtsStatus = (currentStatus, newLanguageTts) => {
    if (newLanguageTts) {
        if (currentStatus === 'n/a') {
            return 'off';
        }
        if (currentStatus === 'stopped' || currentStatus === 'waiting' || currentStatus === 'on') {
            return 'waiting';
        }
        return currentStatus;
    }
    if (currentStatus === 'n/a' || currentStatus === 'stopped') {
        return currentStatus;
    }
    return 'stopped';
};
const SubtitlesPage = ({ eventId }) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const streamRef = useRef();
    const [event, setEvent] = useState('loading');
    const [subtitlesSettings, setSubtitlesSettings] = useState(loadSubtitlesSettings);
    const [content, setContent] = useState('inactive');
    const [controlsVisible, setControlsVisible] = useState(subtitlesSettings.controls === 'on' || subtitlesSettings.controls === 'auto');
    const [autoScroll, setAutoScroll] = useState(true);
    const [ttsStatus, setTtsStatus] = useState('n/a');
    const [selectedLanguage, setSelectedLanguage] = useState(null);
    const [languagePending, setLanguagePending] = useState(true);
    const { intl } = useI18n();
    const languageParam = searchParams.get('lang');
    const language = languageParam?.trim() || null;
    const presenterId = searchParams.get('presenterId')?.trim() || null;
    useEffect(() => {
        if (languageParam === null || language !== null) {
            return;
        }
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('lang');
        navigate({ search: nextParams.toString(), hash: window.location.hash }, { replace: true });
    }, [language, languageParam, navigate, searchParams]);
    useEffect(() => {
        idleTimer.onIdle(() => {
            if (subtitlesSettings.controls === 'auto') {
                setControlsVisible(false);
            }
        });
        idleTimer.onActive(() => {
            if (subtitlesSettings.controls === 'auto') {
                setControlsVisible(true);
            }
        });
        idleTimer.mount();
        return () => {
            idleTimer.unmount();
        };
    }, [subtitlesSettings.controls]);
    useEffect(() => {
        const plausibleScript = document.createElement("script");
        plausibleScript.defer = true;
        plausibleScript.setAttribute("data-domain", "beey.live");
        plausibleScript.src = "https://plausible.io/js/script.manual.hash.outbound-links.js";
        document.head.appendChild(plausibleScript);
        const script = document.createElement("script");
        script.src = "./static/js/pageview.js";
        script.defer = true;
        document.head.appendChild(script);
        return () => {
            plausibleScript.remove();
            script.remove();
        };
    }, []);
    useEffect(() => {
        document.body.style.backgroundColor = subtitlesSettings.bgColor;
        return () => {
            document.body.style.backgroundColor = 'initial';
        };
    }, [subtitlesSettings.bgColor]);
    useEffect(() => {
        const styleId = 'presenter-custom-css';
        let styleEl = document.getElementById(styleId);
        if (subtitlesSettings.customCss === '') {
            styleEl?.remove();
            return;
        }
        if (styleEl === null) {
            styleEl = document.createElement('style');
            styleEl.id = styleId;
            document.head.appendChild(styleEl);
        }
        styleEl.textContent = subtitlesSettings.customCss;
        return () => {
            styleEl?.remove();
        };
    }, [subtitlesSettings.customCss]);
    const navigateToLanguage = (nextLanguage) => {
        console.log('Navigating to language:', nextLanguage);
        const eventHash = window.location.hash;
        const nextParams = new URLSearchParams(searchParams);
        if (nextLanguage === null) {
            nextParams.delete('lang');
        }
        else {
            nextParams.set('lang', nextLanguage);
        }
        const stringParams = nextParams.toString();
        const params = stringParams === '' ? '' : `?${stringParams}`;
        const url = buildUrlPath(`/${params}${eventHash}`);
        navigate(url, { replace: true });
    };
    useEffect(() => {
        let subtitlesStream;
        const initEvent = async () => {
            if (eventId === undefined) {
                return;
            }
            const event = await fetchPublicEventById(eventId);
            setEvent(event);
            if (event === 'not-found') {
                return;
            }
            subtitlesStream = new SubtitlesStream(event.id, presenterId === null ? language : null, presenterId);
            subtitlesStream.on('configSync', (pending) => {
                setLanguagePending(pending);
            });
            subtitlesStream.on('presenterSettingsChanged', (settings) => {
                setSubtitlesSettings((prev) => ({
                    ...prev,
                    ...settings,
                }));
                if (settings.controls === 'off') {
                    setControlsVisible(false);
                }
            });
            subtitlesStream.on('segmentStarted', (segment, selectedLanguage) => {
                setContent({
                    segment,
                    transcript: 'waiting',
                });
                setTtsStatus((prevStatus) => updateTtsStatus(prevStatus, selectedLanguage.tts));
            });
            subtitlesStream.on('languageChanged', (language) => {
                setSelectedLanguage(language.iso);
                setTtsStatus((prevStatus) => updateTtsStatus(prevStatus, language.tts));
                navigateToLanguage(language.iso === streamRef.current?.getOriginalLanguage()
                    ? null
                    : language.iso);
            });
            subtitlesStream.on('segmentEnded', () => {
                setContent('inactive');
            });
            subtitlesStream.on('transcriptUpdated', (transcript) => {
                setContent((prevContent) => {
                    if (prevContent === 'inactive' || prevContent === 'loading') {
                        return prevContent;
                    }
                    return { ...prevContent, transcript };
                });
            });
            subtitlesStream.on('voiceStarted', () => {
                setTtsStatus('on');
            });
            subtitlesStream.connect();
            streamRef.current = subtitlesStream;
        };
        void initEvent();
        return () => {
            subtitlesStream?.disconnect();
            streamRef.current = undefined;
        };
    }, []);
    const goFullScreen = async () => {
        try {
            await document.documentElement.requestFullscreen();
        }
        catch (error) {
            console.error('Native fullscreen failed, using css as fallback.');
        }
    };
    const exitFullScreen = () => {
        if (document.exitFullscreen) {
            document.exitFullscreen();
        }
    };
    const handleSettingsChange = async (changedValues) => {
        const newSettings = {
            ...subtitlesSettings,
            ...changedValues,
        };
        setSubtitlesSettings(newSettings);
        storeSubtitlesSettings(newSettings);
        if ('fullscreen' in changedValues) {
            if (changedValues.fullscreen) {
                void goFullScreen();
            }
            else {
                exitFullScreen();
            }
        }
        if ('theme' in changedValues) {
            const metaThemeColor = document.querySelector('meta[name="theme-color"]');
            if (metaThemeColor) {
                metaThemeColor.setAttribute('content', newSettings.theme === 'dark' ? '#000000' : '#ffffff');
            }
            else {
                const newMetaThemeColor = document.createElement('meta');
                newMetaThemeColor.setAttribute('name', 'theme-color');
                newMetaThemeColor.setAttribute('content', newSettings.theme === 'dark' ? '#000000' : '#ffffff');
                document.head.appendChild(newMetaThemeColor);
            }
            const nextParams = new URLSearchParams(searchParams);
            nextParams.delete('textColor');
            nextParams.delete('bgColor');
            navigate({ search: nextParams.toString(), hash: window.location.hash }, { replace: true });
        }
    };
    const handleLanguageChange = (language) => {
        if (content === 'inactive' || content === 'loading') {
            return;
        }
        streamRef.current?.selectLanguage(language);
    };
    const handleAudioToggle = (toggle) => {
        if (ttsStatus !== 'n/a' && ttsStatus !== 'stopped') {
            setTtsStatus(toggle === 'on' ? 'waiting' : 'off');
            streamRef.current?.setStreamAudio(toggle === 'on');
        }
    };
    let panel = null;
    let latency = 'none';
    if (content === 'loading' || event === 'loading') {
        panel = (_jsx(AlertPanel, { icon: _jsx(Loading3QuartersOutlined, { spin: true }), title: intl.formatMessage({ id: 'loading' }), size: "small" }));
    }
    else if (event === 'not-found') {
        panel = (_jsx(AlertPanel, { icon: _jsx(CloseCircleFilled, {}), title: intl.formatMessage({ id: 'eventNotFound' }), message: intl.formatMessage({ id: 'eventNotFoundDesc' }) }));
    }
    else if (content === 'inactive') {
        if (subtitlesSettings.controls === 'off') {
            panel = _jsx("div", { className: styles.inactivePlaceholder });
        }
        else {
            panel = (_jsx(AlertPanel, { icon: _jsx(AudioMutedOutlined, {}), title: intl.formatMessage({ id: 'noActiveSegment' }), message: intl.formatMessage({ id: 'noActiveSegmentDesc' }) }));
        }
    }
    else {
        latency = isTestingMode()
            ? content.transcript === 'waiting'
                ? 'waiting'
                : content.transcript.latency
            : 'none';
        panel = (_jsx(SubtitlesPanel, { subtitles: content.transcript === 'waiting' ? 'waiting' : content.transcript.subtitles, settings: subtitlesSettings, speechSegmentation: content.segment.speechSegmentation, autoScroll: autoScroll, onAutoScrollChange: setAutoScroll, autoscrollButtonTestId: "subtitles-autoscroll-button", showAutoscrollButton: subtitlesSettings.controls !== 'off' }));
    }
    const showControls = subtitlesSettings.controls !== 'off' && (controlsVisible ||
        subtitlesSettings.controls === 'on' ||
        !autoScroll);
    const footer = content !== 'loading'
        && content !== 'inactive'
        && subtitlesSettings.controls !== 'off'
        ? (_jsx(SubtitlesFooter, { settings: subtitlesSettings, language: selectedLanguage ?? content.segment.audioLanguage, languageLoading: languagePending, onAudioToggle: handleAudioToggle, onSettingsChange: handleSettingsChange, onLanguageChange: handleLanguageChange, segment: content.segment, ttsStatus: ttsStatus, visible: showControls })) : _jsx("footer", {});
    return (_jsxs("div", { "data-theme": subtitlesSettings.theme, className: clsx(styles.subtitlesPage, { [styles.showControls]: showControls }, { [styles.noUserSelect]: autoScroll }), style: { backgroundColor: subtitlesSettings.bgColor }, children: [_jsx("div", { className: styles.header, children: _jsx(SubtitlesHeader, { latency: latency }) }), panel, footer] }));
};
export default SubtitlesPage;
